<template>
  <v-card>
    <v-toolbar :color="collapsed ? 'grey-lighten-2' : 'primary'" class="elevation-0">
      <v-toolbar-title class="text-subtitle-1 font-weight-medium flex-0-0">US Sales Overview</v-toolbar-title>
      <v-icon class="ml-1" style="cursor: pointer" @click="collapsed = !collapsed">{{
        collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up'
      }}</v-icon>
      <v-spacer></v-spacer>
      <DateRangePicker
        class="mr-4"
        v-show="!collapsed"
        :short="true"
        default="thisMonth"
        @change="loadOrderData"
        align="right"
      ></DateRangePicker>
    </v-toolbar>
    <v-expand-transition>
      <v-data-table
        v-show="!collapsed"
        hide-default-footer
        :items-per-page="-1"
        :headers="headers"
        :items="tableData"
        :no-data-text="noDataText"
        :loading="loading"
        :sort-by="[{ key: 'creatorOrdersTotal', order: 'desc' }]"
      >
        <template v-slot:item="props">
          <tr>
            <td>{{ props.item.email }}</td>
            <td>
              {{ props.item.numQuotes }}
            </td>
            <td>
              {{ props.item.numOrdersExcRecuts }}<small class="ml-1">({{ props.item.numOrdersIncRecuts }})</small>
            </td>
            <td class="text-right">${{ $numeral(props.item.creatorOrdersTotal).format('0,0.00') }}</td>
          </tr>
        </template>
        <template v-slot:[`body.append`]>
          <tr>
            <td><strong>Totals</strong></td>
            <td>
              <strong>{{ totals.quotes }}</strong>
            </td>
            <td>
              <strong
                >{{ totals.ordersExcRecuts }}<small class="ml-1">({{ totals.ordersIncRecuts }})</small></strong
              >
            </td>
            <td class="text-right">
              <strong>${{ $numeral(totals.ordersTotalTally).format('0,0.00') }}</strong>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-expand-transition>
  </v-card>
</template>

<script>
import DateRangePicker from '../ui/DateRangePicker.vue'
import { db } from '@/js/db'
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'

export default {
  name: 'QuotePeriodOverview',
  components: { DateRangePicker },
  data() {
    return {
      loading: true,
      collapsed: true,
      filteredOrders: [],
      createdQuotes: [],
      headers: [
        {
          title: 'User',
          key: 'email',
          sortable: false
        },
        {
          title: 'Quotes Created',
          key: 'numQuotes',
          sortable: false
        },
        {
          title: 'Orders Paid ex. Recuts (in. Recuts)',
          key: 'numOrdersIncRecuts',
          sortable: false
        },
        {
          title: 'Orders Total',
          key: 'creatorOrdersTotal',
          sortable: false,
          align: 'end'
        }
      ],
      tableData: [],
      totals: {}
    }
  },
  computed: {
    noDataText() {
      return this.loading ? '' : 'No Data'
    }
  },
  methods: {
    async loadOrderData(range) {
      this.loading = true

      // 0. // clear previous data
      this.filteredOrders = []
      this.createdQuotes = []

      // 1. // set the date range to month
      let months = []
      let dateStart = this.$moment(range[0])
      let dateEnd = this.$moment(range[1]).endOf('day')

      while (dateEnd > dateStart || dateStart.format('M') === dateEnd.format('M')) {
        months.push(dateStart.format('MMM YY'))
        dateStart.add(1, 'month')
      }

      // find the startTimestamp & endTimestamp date of the month
      const startTimestamp = this.$utils.tsFromDate(range[0].toDate())
      const endTimestamp = this.$utils.tsFromDate(range[1].clone().endOf('day').toDate())

      let ordersSnap = await getDocs(
        query(
          collection(db, 'orders'),
          where('foc', '==', false),
          where('type', '==', 'mega-admin'),
          where('region', '==', 'us'), // Changed to 'us' for US sales overview
          where('dateCreated', '>=', startTimestamp) // cant be created as an order before the first payment
        )
      )

      // filter orders based on payments dated after the start date
      ordersSnap.forEach((snap) => {
        const order = snap.data()

        if (!order.payments || order.payments.length === 0 || !order.payments[0].date || !order.payments[0].amount) {
          return // skip orders with no payments or first payment date/amount
        }

        let firstPayment = order?.payments?.[0]
        // check if firstPayment exists and has a valid amount and within the date range
        if (
          firstPayment &&
          firstPayment?.amount > 0 &&
          firstPayment?.date >= startTimestamp &&
          firstPayment?.date <= endTimestamp
        ) {
          // add order to filteredOrders; include firstPaymentDate << not used in the table
          this.filteredOrders.push({ id: snap.id, ...order, firstPaymentDate: firstPayment.date })
        }
      })

      // 3. // getting quotes data by date created
      let indexSnap = await getDocs(
        query(
          collection(db, 'quoteIndex'),
          where('region', '==', 'us'), // Changed to 'us' for US sales overview
          where('dateCreated', '>=', startTimestamp),
          where('dateCreated', '<=', endTimestamp)
        )
      )

      // Extract quote IDs (assuming snap.id is the quote ID)
      const quoteIndexIDs = indexSnap.docs.map((snap) => snap.id)

      // Fetch all quote documents in parallel
      const createdQuoteFetches = quoteIndexIDs.map((id) => getDoc(doc(db, 'quotes', id)))
      // Await all fetches and assign
      const createdQuoteDocs = await Promise.all(createdQuoteFetches)

      this.createdQuotes = createdQuoteDocs
        .filter((snap) => snap.exists()) // quoteIndex can point at a deleted quote
        .map((snap) => ({
          id: snap.id,
          ...snap.data()
        }))

      this.loading = false
      // Process the data
      this.processData()
    },
    processData() {
      this.loading = true
      this.totals = {
        quotes: 0,
        ordersExcRecuts: 0,
        ordersIncRecuts: 0,
        ordersTotalTally: 0
      }

      let o = {}

      // COUNTING QUOTES
      this.createdQuotes.forEach((quote) => {
        this.totals.quotes++

        const creator = quote.creatorEmail

        if (!o[creator]) {
          o[creator] = {
            email: creator,
            numQuotes: 1,
            numOrdersExcRecuts: 0,
            numOrdersIncRecuts: 0,
            creatorOrdersTotal: 0
          }
        } else {
          o[creator].numQuotes++
        }
      })

      // COUNTING ORDERS & its TOTAL
      this.filteredOrders.forEach((order) => {
        // separate order by recut or not
        let isRecut = order?.recut || false

        if (isRecut) {
          this.totals.ordersIncRecuts++
        } else {
          this.totals.ordersIncRecuts++
          this.totals.ordersExcRecuts++
        }

        const creator = order.creatorEmail

        // if the creator is not in the object, add them
        if (!o[creator]) {
          o[creator] = {
            email: creator,
            numQuotes: 0,
            numOrdersExcRecuts: 0,
            numOrdersIncRecuts: 0,
            creatorOrdersTotal: 0
          }
        }

        // increment order counts
        if (isRecut) {
          o[creator].numOrdersIncRecuts++
        } else {
          o[creator].numOrdersIncRecuts++
          o[creator].numOrdersExcRecuts++
        }

        // add the order total to the creator's total
        o[creator].creatorOrdersTotal += order.total
        this.totals.ordersTotalTally += order.total
      })

      this.loading = false
      this.tableData = Object.values(o)
    }
  }
}
</script>
